
import { supabase } from "@/integrations/supabase/client";
import { VoiceConfig } from "@/types/admin";

export const fetchVoiceConfigs = async (): Promise<VoiceConfig[]> => {
  try {
    const { data, error } = await supabase
      .from('voice_configs')
      .select('*');
    
    if (error) throw error;
    return data as VoiceConfig[];
  } catch (error) {
    console.error("Error fetching voice configs:", error);
    return [];
  }
};

export const fetchDefaultVoice = async (): Promise<VoiceConfig | null> => {
  try {
    const { data, error } = await supabase
      .from('voice_configs')
      .select('*')
      .eq('is_default', true)
      .single();
    
    if (error) throw error;
    return data as VoiceConfig;
  } catch (error) {
    console.error("Error fetching default voice config:", error);
    return null;
  }
};

export const setDefaultVoice = async (voiceId: string): Promise<{ success: boolean; error?: string }> => {
  try {
    // First, reset all voices to non-default
    const { error: resetError } = await supabase
      .from('voice_configs')
      .update({ is_default: false })
      .not('id', 'is', null);
    
    if (resetError) throw resetError;
    
    // Then set the selected voice as default
    const { error: updateError } = await supabase
      .from('voice_configs')
      .update({ is_default: true })
      .eq('id', voiceId);
    
    if (updateError) throw updateError;
    
    return { success: true };
  } catch (error) {
    console.error("Error setting default voice:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "Unknown error"
    };
  }
};

export const createVoiceConfig = async (config: VoiceConfig): Promise<{ success: boolean; error?: string; id?: string }> => {
  try {
    const { data, error } = await supabase
      .from('voice_configs')
      .insert([config])
      .select();
    
    if (error) throw error;
    
    return { 
      success: true,
      id: data?.[0]?.id 
    };
  } catch (error) {
    console.error("Error creating voice config:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "Unknown error"
    };
  }
};

export const updateVoiceConfig = async (
  voiceId: string, 
  data: Partial<VoiceConfig>
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('voice_configs')
      .update(data)
      .eq('id', voiceId);
    
    if (error) throw error;
    
    return { success: true };
  } catch (error) {
    console.error("Error updating voice config:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "Unknown error"
    };
  }
};

export const deleteVoiceConfig = async (
  voiceId: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const { error } = await supabase
      .from('voice_configs')
      .delete()
      .eq('id', voiceId);
    
    if (error) throw error;
    
    return { success: true };
  } catch (error) {
    console.error("Error deleting voice config:", error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : "Unknown error"
    };
  }
};
